// ./src/components/features/Highlights/HighlightsFilterBar/HighlightsFilterBar.tsx
//
// Filter controls for the Highlights list: by status (active / disabled) and by
// group (amenity / policy / …). Fully controlled — the page owns the value and
// maps it to a HighlightFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { HIGHLIGHT_GROUP_VALUES } from "@/utils/api";
import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

export type HighlightStatusFilter = "active" | "disabled";

export interface HighlightsFilterValue {
  status: HighlightStatusFilter | null;
  group: string | null;
}

export interface HighlightsFilterBarProps {
  value: HighlightsFilterValue;
  onChange: (value: HighlightsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_HIGHLIGHTS_FILTER: HighlightsFilterValue = {
  status: null,
  group: null,
};

const isFilterActive = (value: HighlightsFilterValue): boolean => Boolean(value.status || value.group);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };
const GROUP_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const ANY = "any";

const HighlightsFilterBarComponent: React.FC<HighlightsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["highlights", "common"]);

  const statusOptions = [
    { value: ANY, label: t("highlights:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("highlights:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("highlights:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const groupOptions = HIGHLIGHT_GROUP_VALUES.map((group) => ({
    value: group,
    label: t(`highlights:groups.${group}`, { defaultValue: group.replace(/_/g, " ") }),
  }));

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("highlights:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as HighlightStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={GROUP_STYLE}>
        <SelectField
          label={t("highlights:filters.group.label", { defaultValue: "Group" })}
          placeholder={t("highlights:filters.group.placeholder", { defaultValue: "All groups" })}
          data={groupOptions}
          value={value.group}
          onChange={(_name, group) => onChange({ ...value, group })}
          disabled={disabled}
          clearable
          comboboxProps={DRAWER_COMBOBOX_PROPS}
          size="md"
          mt={4}
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_HIGHLIGHTS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const HighlightsFilterBar = React.memo(HighlightsFilterBarComponent);
